import { useLayoutEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { fetchAgencyConfiguration } from '@ngsos/portal-core/agency';

import { emsApi } from '@/api/emsApi';
import { SplashscreenComponent } from '@/app/shell/AuthenticationPageLayout';
import { AgencyConfigurationContextProvider, useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import { applyAndRememberAgencyThemeColor } from '@/app/shell/agencyThemeColor';
import { NgButton } from '@/ui';
import { AgencyConfigurationContext as UiAgencyConfigurationContext } from '@/ui/agency/AgencyConfigurationContext';

const DEFAULT_FAVICON = '/Images/Icons/icon-72x72.png';

function useAgencyThemeColor(themeColor: string | undefined) {
  useLayoutEffect(() => {
    if (themeColor === undefined || themeColor.length === 0) {
      return;
    }

    applyAndRememberAgencyThemeColor(themeColor);
  }, [themeColor]);
}

export function AgencyConfigurationProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const {
    data: agencyConfiguration,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['configuration'],
    queryFn: () => fetchAgencyConfiguration(emsApi),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const agencyLogoIcon = agencyConfiguration
    ? `${agencyConfiguration.agencyPortalConfiguration.logoFolderPath}/logo.ico`
    : DEFAULT_FAVICON;
  useAgencyThemeColor(agencyConfiguration?.agencyPortalConfiguration.themeColor);

  return (
    <>
      <link rel="icon" href={agencyLogoIcon} type="image/x-icon" />

      {agencyConfiguration ? (
        <AgencyConfigurationContextProvider value={agencyConfiguration}>
          <UiAgencyConfigurationContext.Provider value={agencyConfiguration}>
            {children}
          </UiAgencyConfigurationContext.Provider>
        </AgencyConfigurationContextProvider>
      ) : isError ? (
        <SplashscreenComponent>
          <div>
            <p role="alert">{t('Loading_GeneralLoadingFailedMessage')}</p>
            <div>
              <NgButton
                text={t('Button_TryAgain')}
                icon="fal fa-rotate-right"
                disabled={isFetching}
                onClick={() => {
                  void refetch();
                }}
              />
            </div>
          </div>
        </SplashscreenComponent>
      ) : (
        <SplashscreenComponent>
          <p className="fade-in-delay">Loading agency configuration</p>
        </SplashscreenComponent>
      )}
    </>
  );
}

type AgencyViewProps = {
  agencyId?: string;
  agencyIds?: readonly string[];
  children: ReactNode;
};

export function AgencyView({ agencyId, agencyIds, children }: AgencyViewProps) {
  const agencyConfiguration = useAgencyConfiguration();

  const effectiveAgencyIds = [...(agencyIds ?? []), ...(agencyId !== undefined ? [agencyId] : [])];
  if (effectiveAgencyIds.length === 0) {
    throw new Error('At least one of the parameters agencyId or agencyIds must be set.');
  }

  const isVisible = effectiveAgencyIds.some(
    (id) => id.toLowerCase() === agencyConfiguration.id.toLowerCase(),
  );

  return isVisible ? <>{children}</> : null;
}
